import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSprintsCatalogo } from '@/hooks/useObras';
import {
  useEficienciaCrono,
  useGuardarEficienciaCrono,
  useGuardarTipoCasaSprints,
  useTiposCasaSprints,
} from '@/hooks/useTiposCasa';
import { cn } from '@/lib/utils';
import type { SprintCatalogo, TipoCasa, TipoCasaSprints } from '@adelante/shared';
import { CircleNotch, Gauge, House, Pause, PencilSimple } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const DESC: Record<TipoCasa, string> = {
  '1N-Techo': 'Casa 1 nivel con techo inclinado',
  '1N-Azotea': 'Casa 1 nivel con azotea',
  '2N-Techo': 'Casa 2 niveles con techo',
  '2N-Azotea': 'Casa 2 niveles con azotea',
};
const ORDEN_TIPOS: TipoCasa[] = ['1N-Techo', '1N-Azotea', '2N-Techo', '2N-Azotea'];

/** Peso de referencia por sprint = 100% / total (string). */
function pesoRef(total: number): string {
  return total > 0 ? `${(100 / total).toFixed(1)}% por sprint` : '—';
}

/**
 * Admin · Tipos de Casa — qué sprints (de la secuencia global) participan en
 * cada tipo de casa, su total y el peso de referencia por sprint. Editable.
 */
export function TiposCasaPantalla() {
  const tiposQ = useTiposCasaSprints();
  const catalogoQ = useSprintsCatalogo();
  const [editando, setEditando] = useState<TipoCasaSprints | null>(null);
  const eficiencia = useEficienciaCrono().data?.data?.eficiencia ?? null;

  const tipos = tiposQ.data?.data ?? [];
  const catalogo = catalogoQ.data?.data ?? [];
  const ordenadas = [...tipos].sort(
    (a, b) => ORDEN_TIPOS.indexOf(a.tipo_casa) - ORDEN_TIPOS.indexOf(b.tipo_casa),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tipos de casa</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Qué sprints (de la secuencia global) participan en cada tipo de casa. El peso de
          referencia por sprint = 100% / total de sprints.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <House className="h-5 w-5" weight="bold" aria-hidden="true" />
            Configuración por tipo de casa
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(tiposQ.isLoading || catalogoQ.isLoading) && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando…
            </div>
          )}
          {tiposQ.error && <p className="py-8 text-destructive">Error: {tiposQ.error.message}</p>}

          {!tiposQ.isLoading && !catalogoQ.isLoading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo de casa</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-center">Total sprints</TableHead>
                  <TableHead>Peso de referencia</TableHead>
                  <TableHead>Meta semanal</TableHead>
                  <TableHead>Sprints permitidos</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordenadas.map((t) => (
                  <TableRow key={t.tipo_casa}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {t.tipo_casa}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {DESC[t.tipo_casa]} — {t.sprints.length} semanas
                    </TableCell>
                    <TableCell className="text-center text-lg font-semibold tabular-nums">
                      {t.sprints.length}
                    </TableCell>
                    <TableCell className="text-sm">{pesoRef(t.sprints.length)}</TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {eficiencia != null && t.sprints.length > 0
                        ? `${((100 / t.sprints.length) * (eficiencia / 100)).toFixed(2)}%`
                        : '—'}
                    </TableCell>
                    <TableCell className="max-w-md text-xs text-muted-foreground">
                      {t.sprints.join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setEditando(t)}>
                        <PencilSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EficienciaCard />

      <EditarDialog tipo={editando} catalogo={catalogo} onClose={() => setEditando(null)} />
    </div>
  );
}

/**
 * Eficiencia (%) de la meta de % Crono Esperado del reporte semanal:
 * meta = (100 / sprints del tipo) × eficiencia × días efectivos / 5.
 */
function EficienciaCard() {
  const q = useEficienciaCrono();
  const guardar = useGuardarEficienciaCrono();
  const actual = q.data?.data?.eficiencia ?? null;
  const [valor, setValor] = useState('');

  useEffect(() => {
    if (actual != null) setValor(String(actual));
  }, [actual]);

  const num = Number(valor);
  const valido = valor.trim() !== '' && Number.isFinite(num) && num >= 1 && num <= 100;

  async function handleGuardar() {
    if (!valido) return;
    try {
      await guardar.mutateAsync(num);
      toast.success(`Eficiencia guardada: ${num}%`);
    } catch (e) {
      toast.error('No se pudo guardar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" weight="bold" aria-hidden="true" />
          Eficiencia de la meta de cronograma
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          El <strong>% Crono Esperado</strong> del reporte semanal es la meta de cada casa según su
          tipo: un sprint por semana (100% / total de sprints) × eficiencia × días efectivos / 5. Un
          cambio aplica a la semana abierta y a las que se cierren en adelante; cada semana cerrada
          conserva la eficiencia que tenía al cerrarse.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="eficiencia-crono">Eficiencia (%)</Label>
            <Input
              id="eficiencia-crono"
              type="number"
              min={1}
              max={100}
              step={0.5}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="w-32"
            />
          </div>
          <Button onClick={handleGuardar} disabled={!valido || guardar.isPending || num === actual}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
          {!valido && valor !== '' && (
            <span className="text-xs text-destructive">Debe estar entre 1 y 100.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function EditarDialog({
  tipo,
  catalogo,
  onClose,
}: {
  tipo: TipoCasaSprints | null;
  catalogo: SprintCatalogo[];
  onClose: () => void;
}) {
  const guardar = useGuardarTipoCasaSprints();
  const [sel, setSel] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (tipo) setSel(new Set(tipo.sprints));
  }, [tipo]);

  function toggle(n: number) {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  }

  async function handleGuardar() {
    if (!tipo) return;
    try {
      await guardar.mutateAsync({
        tipo: tipo.tipo_casa,
        body: { sprints: [...sel] },
      });
      toast.success(`${tipo.tipo_casa}: ${sel.size} sprints guardados`);
      onClose();
    } catch (e) {
      toast.error('No se pudo guardar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  const total = sel.size;

  return (
    <Dialog open={tipo !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Sprints de {tipo?.tipo_casa}</DialogTitle>
          <DialogDescription>
            Marcá los sprints (de la secuencia global) que participan en este tipo de casa. Total:{' '}
            <strong>{total}</strong> · peso de referencia <strong>{pesoRef(total)}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {catalogo
            .slice()
            .sort((a, b) => a.numero_global - b.numero_global)
            .map((c) => {
              const checked = sel.has(c.numero_global);
              return (
                <button
                  key={c.numero_global}
                  type="button"
                  onClick={() => toggle(c.numero_global)}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                    checked
                      ? 'border-adelante-primary bg-adelante-primary/10'
                      : 'border-border bg-white hover:bg-zinc-50',
                  )}
                >
                  <Checkbox checked={checked} aria-hidden="true" className="pointer-events-none" />
                  <span className="min-w-0">
                    <span className="font-mono font-semibold">{c.numero_global}</span>{' '}
                    <span className="text-muted-foreground">{c.nombre}</span>
                    {c.es_espera && (
                      <Pause
                        className="ml-1 inline h-3 w-3 text-muted-foreground"
                        weight="fill"
                        aria-label="Espera"
                      />
                    )}
                  </span>
                </button>
              );
            })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardar.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardar.isPending}>
            {guardar.isPending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Guardando…
              </>
            ) : (
              'Guardar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
